import React, { useState, useContext, useEffect } from "react";
import Modal from "@components/modal";
import { PrimaryButton } from "@acko-tech/building-blocks.ui.button";
import { ChildrenWrapper } from "@components/modal/styles";
import { getIsVisibleHook } from "@utils/hooks/getIsVisibleHook";
import { useOnload } from "@utils/useOnLoad";
import Hyperlink from "@components/common/hyperlink";
import { Context } from "@components/useContext/context";
import { useSelector } from "react-redux";
import {
  InsuranceOptionCard,
  PopularTag,
  TableHeader,
  SumInsured,
  Price,
  Slabs,
  Subheading,
  HyperlinkStyles
} from "./styles";
import { getInrFormat } from "@utils/inrFormatter";

interface SlabOption {
  sum_insured: number;
  net_premium: number;
}

interface Props {
  is_visible: boolean;
  actions: any;
  heading: string;
  headingElectricalAccessory?: string;
  headingNonElectricalAccessory?: string;
  subHeadingElectricalAccessoryElectricVehicle: string;
  subHeadingElectricalAccessoryNonElectricVehicle: string;
  subHeadingNonElectricalAccessory: string;
  primaryCtaChooseAccessory: string;
  addonId: string;
  id: string;
  errorMessageNoSlabSelected: string;
  primaryCtaUpdateAccessory: string;
  primaryCtaKeepAccessory: string;
  isSelected: boolean;
  sumInsuredSlabs: SlabOption[];
  selectedSumInsured: number;
  removeText: string;
  popularSlab: number;
}

const AddonAccessoryModal: React.FC<Props> = ({
  actions,
  heading,
  headingElectricalAccessory,
  headingNonElectricalAccessory,
  subHeadingElectricalAccessoryElectricVehicle,
  subHeadingElectricalAccessoryNonElectricVehicle,
  subHeadingNonElectricalAccessory,
  primaryCtaChooseAccessory,
  addonId,
  id,
  primaryCtaKeepAccessory,
  primaryCtaUpdateAccessory,
  sumInsuredSlabs,
  selectedSumInsured,
  removeText,
  popularSlab
}) => {
  const is_visible = getIsVisibleHook({ id });
  const fuelType = useSelector((state: any) => state.globalState.fueltype);

  const [selectedSlabAmount, setSelectedSlabAmount] = useState<number | null>(
    null
  );

  const subHeading =
    addonId === "non_electrical_accessories_own_damage_basic_car"
      ? subHeadingNonElectricalAccessory
      : fuelType === "electric"
        ? subHeadingElectricalAccessoryElectricVehicle
        : subHeadingElectricalAccessoryNonElectricVehicle;
  const modalHeading =
    addonId === "non_electrical_accessories_own_damage_basic_car"
      ? (headingNonElectricalAccessory || heading)
      : (headingElectricalAccessory || heading);

  useEffect(() => {
    if (selectedSumInsured) {
      setSelectedSlabAmount(selectedSumInsured);
    } else {
      setSelectedSlabAmount(popularSlab);
    }
  }, [selectedSumInsured, popularSlab]);

  const { on_close, on_load, on_click, on_remove } = actions;
  const { isDesktop } = useContext(Context);

  const handleClose = () => {
    if (on_close?.length > 0) {
      on_close?.forEach((eachFunc: any) => {
        typeof eachFunc.funCall === "function" &&
          eachFunc.funCall(eachFunc.args);
      });
    }
  };

  useOnload({ on_load, is_visible });

  const primaryCtaText =
    selectedSumInsured > 0 && selectedSlabAmount === selectedSumInsured
      ? primaryCtaKeepAccessory
      : selectedSumInsured > 0 && selectedSlabAmount !== selectedSumInsured
        ? primaryCtaUpdateAccessory
        : primaryCtaChooseAccessory;

  return (
    <Modal
      showModal={is_visible}
      styles={{ width: "500px" }}
      is_visible={is_visible}
      onClose={handleClose}
      modalHeading={modalHeading}
    >
      <ChildrenWrapper>
        <Subheading isDesktop={isDesktop}>{subHeading}</Subheading>
        <div>
          <TableHeader>
            <span>SUM INSURED</span>
            <span>ADD-ON PRICE</span>
          </TableHeader>

          <Slabs>
            <div>
              {Array.isArray(sumInsuredSlabs) &&
                sumInsuredSlabs.length > 0 &&
                sumInsuredSlabs.map((slab) => {
                  return (
                    <InsuranceOptionCard
                      isDesktop={isDesktop}
                      key={slab.sum_insured}
                      isSelected={selectedSlabAmount === slab.sum_insured}
                      onClick={() => {
                        setSelectedSlabAmount(slab.sum_insured);
                      }}
                    >
                      {slab.sum_insured === popularSlab && (
                        <PopularTag>Popular</PopularTag>
                      )}
                      <SumInsured>₹{getInrFormat(slab.sum_insured)}</SumInsured>
                      <Price>₹{getInrFormat(slab.net_premium)}</Price>
                    </InsuranceOptionCard>
                  );
                })}
            </div>
          </Slabs>
        </div>

        <div>
          <PrimaryButton
            isFullWidth
            onClick={() => {
              const sumAssuredValue = selectedSlabAmount;

              if (
                selectedSumInsured > 0 &&
                selectedSlabAmount === selectedSumInsured
              ) {
                if (on_close?.length > 0) {
                  on_close?.forEach((eachFunc: any) => {
                    typeof eachFunc.funCall === "function" &&
                      eachFunc.funCall(eachFunc.args);
                  });
                }
              } else {
                if (on_click?.length > 0) {
                  on_click?.forEach((eachFunc: any) => {
                    typeof eachFunc.funCall === "function" &&
                      eachFunc.funCall({
                        ...eachFunc.args,
                        data: {
                          id: addonId,
                          interaction: "add",
                          sum_assured_value: sumAssuredValue
                        },
                        customData: {
                          sum_assured_value: sumAssuredValue
                        }
                      });
                  });
                }
              }
            }}
          >
            {primaryCtaText}
          </PrimaryButton>
          {selectedSumInsured > 0 && (
            <Hyperlink
              text={removeText}
              type={"DANGER"}
              styles={HyperlinkStyles}
              clickHandler={() => {
                if (on_remove?.length > 0) {
                  on_remove.forEach((eachFunc: any) => {
                    typeof eachFunc.funCall === "function" &&
                      eachFunc.funCall(eachFunc.args);
                  });
                }
                handleClose();
              }}
            />
          )}
        </div>
      </ChildrenWrapper>
    </Modal>
  );
};

export default AddonAccessoryModal;
